import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, Share2, Trash2 } from 'lucide-react';
import { adminSiteSettingsService } from '../../../services/adminSiteSettingsService';
import { Button, Input } from '../../shared';
import { useToastContext } from '../../../contexts/ToastContext';

const DEFAULT_PLATFORMS = [
 { key: 'instagram', label: 'Instagram', placeholder: '@yourhandle', is_active: true, is_required: true, sort_order: 1 },
 { key: 'facebook', label: 'Facebook', placeholder: 'page name or @handle', is_active: true, is_required: false, sort_order: 2 },
 { key: 'tiktok', label: 'TikTok', placeholder: '@yourhandle', is_active: true, is_required: false, sort_order: 3 },
 { key: 'x', label: 'X (Twitter)', placeholder: '@yourhandle', is_active: true, is_required: false, sort_order: 4 },
 { key: 'youtube', label: 'YouTube', placeholder: '@channel', is_active: true, is_required: false, sort_order: 5 },
 { key: 'linkedin', label: 'LinkedIn', placeholder: 'company or profile', is_active: false, is_required: false, sort_order: 6 },
];

const normalizeKey = (value) =>
 String(value || '')
 .toLowerCase()
 .replace(/[^a-z0-9_]+/g, '')
 .slice(0, 50);

const VendorSocialPlatformsSettings = () => {
 const queryClient = useQueryClient();
 const toast = useToastContext();
 const [platforms, setPlatforms] = useState(DEFAULT_PLATFORMS);

 const { data: settingsResponse, isLoading } = useQuery({
 queryKey: ['adminSiteSettings'],
 queryFn: () => adminSiteSettingsService.get(),
 });

 useEffect(() => {
 const list = settingsResponse?.data?.vendor_social_platforms;
 if (Array.isArray(list) && list.length > 0) {
 setPlatforms(list);
 }
 }, [settingsResponse]);

 const saveMutation = useMutation({
 mutationFn: (data) => adminSiteSettingsService.save(data),
 onSuccess: () => {
 queryClient.invalidateQueries(['adminSiteSettings']);
 queryClient.invalidateQueries(['siteSettings']);
 toast.success(null, 'Vendor social platforms saved');
 },
 onError: (error) => {
 toast.error(null, error.response?.data?.message || 'Failed to save social platforms');
 },
 });

 const updatePlatform = (index, field, value) => {
 setPlatforms((prev) =>
 prev.map((platform, i) => {
 if (i !== index) return platform;
 const next = { ...platform, [field]: value };
 if (field === 'key') {
 next.key = normalizeKey(value);
 }
 if (next.key === 'instagram') {
 next.is_active = true;
 next.is_required = true;
 }
 return next;
 })
 );
 };

 const addPlatform = () => {
 setPlatforms((prev) => [
 ...prev,
 {
 key: '',
 label: '',
 placeholder: '@yourhandle',
 is_active: true,
 is_required: false,
 sort_order: prev.length + 1,
 },
 ]);
 };

 const removePlatform = (index) => {
 setPlatforms((prev) => {
 const target = prev[index];
 if (target?.key === 'instagram') {
 toast.error(null, 'Instagram is required and cannot be removed');
 return prev;
 }
 return prev.filter((_, i) => i !== index).map((platform, i) => ({
 ...platform,
 sort_order: i + 1,
 }));
 });
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 const cleaned = platforms
 .map((platform, index) => ({
 key: normalizeKey(platform.key || platform.label),
 label: String(platform.label || '').trim(),
 placeholder: String(platform.placeholder || '@yourhandle').trim() || '@yourhandle',
 is_active: platform.key === 'instagram' ? true : Boolean(platform.is_active),
 is_required: platform.key === 'instagram' ? true : Boolean(platform.is_required),
 sort_order: Number(platform.sort_order) || index + 1,
 }))
 .filter((platform) => platform.key && platform.label);

 if (!cleaned.some((platform) => platform.key === 'instagram')) {
 toast.error(null, 'Instagram must stay in the list');
 return;
 }

 const keys = cleaned.map((platform) => platform.key);
 if (new Set(keys).size !== keys.length) {
 toast.error(null, 'Each platform key must be unique');
 return;
 }

 const fullSettings = {
 ...(settingsResponse?.data || {}),
 vendor_social_platforms: cleaned,
 };
 saveMutation.mutate(fullSettings);
 };

 if (isLoading) {
 return <div className="text-sm text-gray-500">Loading social platforms…</div>;
 }

 return (
 <form onSubmit={handleSubmit} className="space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted/70 px-4 py-3">
 <div className="flex items-start gap-3">
 <Share2 className="w-5 h-5 text-ink mt-0.5" />
 <div>
 <h3 className="text-sm font-semibold text-ink">
 Vendor verification social platforms
 </h3>
 <p className="mt-1 text-sm text-ink-secondary">
 Choose which platforms vendors can select when verifying. Instagram stays required.
 Vendors enter a handle for each selected platform.
 </p>
 </div>
 </div>
 </div>

 <div className="space-y-3">
 {platforms.map((platform, index) => {
 const locked = platform.key === 'instagram';
 return (
 <div
 key={`${platform.key || 'new'}-${index}`}
 className="rounded-xl border border-border bg-surface p-4 space-y-3"
 >
 <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
 <Input
 label="Key"
 value={platform.key}
 onChange={(e) => updatePlatform(index, 'key', e.target.value)}
 placeholder="instagram"
 disabled={locked}
 />
 <Input
 label="Label"
 value={platform.label}
 onChange={(e) => updatePlatform(index, 'label', e.target.value)}
 placeholder="Instagram"
 />
 <Input
 label="Handle placeholder"
 value={platform.placeholder}
 onChange={(e) => updatePlatform(index, 'placeholder', e.target.value)}
 placeholder="@yourhandle"
 />
 <Input
 label="Sort order"
 type="number"
 value={platform.sort_order}
 onChange={(e) => updatePlatform(index, 'sort_order', e.target.value)}
 />
 </div>
 <div className="flex flex-wrap items-center justify-between gap-3">
 <div className="flex flex-wrap items-center gap-4 text-sm text-ink-secondary">
 <label className="inline-flex items-center gap-2">
 <input
 type="checkbox"
 checked={Boolean(platform.is_active)}
 disabled={locked}
 onChange={(e) => updatePlatform(index, 'is_active', e.target.checked)}
 className="rounded border-gray-300"
 />
 Active for vendors
 </label>
 <label className="inline-flex items-center gap-2">
 <input
 type="checkbox"
 checked={Boolean(platform.is_required)}
 disabled={locked}
 onChange={(e) => updatePlatform(index, 'is_required', e.target.checked)}
 className="rounded border-gray-300"
 />
 Required at verification
 </label>
 {locked && (
 <span className="text-xs text-amber-700">
 Instagram is always required
 </span>
 )}
 </div>
 <button
 type="button"
 onClick={() => removePlatform(index)}
 disabled={locked}
 className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 disabled:opacity-40"
 >
 <Trash2 className="w-4 h-4" />
 Remove
 </button>
 </div>
 </div>
 );
 })}
 </div>

 <div className="flex flex-wrap items-center gap-3">
 <Button type="button" variant="outline" onClick={addPlatform} className="rounded-md">
 <Plus className="w-4 h-4 mr-2" />
 Add platform
 </Button>
 <Button type="submit" disabled={saveMutation.isPending} className="rounded-md">
 <Save className="w-4 h-4 mr-2" />
 {saveMutation.isPending ? 'Saving…' : 'Save platforms'}
 </Button>
 </div>
 </form>
 );
};

export default VendorSocialPlatformsSettings;
